/**
 * VisitStatusPill — canonical visit status color config and reusable pill component.
 *
 * Single source of truth for visit lifecycle status badge styling.
 * Covers: SCHEDULED, INPROGRESS, DOCPENDING, READYFORBILLING, BILLED, COMPLETE, CANCELLED.
 *
 * Usage (component):
 *   <VisitStatusPill status="INPROGRESS" />
 *   <VisitStatusPill status="BILLED" className="ml-2" />
 *
 * Usage (utilities):
 *   getVisitStatusConfig('DOCPENDING')          → full config object
 *   getVisitStatusPillClassName('BILLED')       → Tailwind class string
 *
 * Color scheme (pastel bg + colored text + colored border):
 *   Scheduled       → violet-100 / violet-700  / violet-300
 *   In Progress     → emerald-600/ emerald-700 / white (solid, matches StatusPill Active)
 *   Doc Pending     → amber-100  / amber-800   / amber-300
 *   Ready Billing   → emerald-100/ emerald-800 / emerald-300
 *   Billed          → slate-200  / slate-700   / slate-400
 *   Cancelled       → stone-100  / stone-600   / stone-200
 */

import React from 'react';

// ─── Config type ──────────────────────────────────────────────────────────────

export interface VisitStatusConfig {
  /** Display label (e.g. "In Progress", "Doc Pending") */
  label: string;
  /** Full Tailwind class string for the pill element */
  className: string;
}

// ─── Canonical map ────────────────────────────────────────────────────────────

const BASE = 'font-semibold text-xs rounded border';

const VISIT_STATUS_MAP: Record<string, VisitStatusConfig> = {
  SCHEDULED:       { label: 'Scheduled',        className: `bg-violet-100 text-violet-700 border-violet-300 ${BASE}` },
  INPROGRESS:      { label: 'In Progress',      className: `bg-emerald-600 border-emerald-700 text-white ${BASE}` },
  IN_PROGRESS:     { label: 'In Progress',      className: `bg-emerald-600 border-emerald-700 text-white ${BASE}` },
  DOCPENDING:      { label: 'Doc Pending',      className: `bg-amber-100 text-amber-800 border-amber-300 ${BASE}` },
  DOC_PENDING:     { label: 'Doc Pending',      className: `bg-amber-100 text-amber-800 border-amber-300 ${BASE}` },
  READYFORBILLING: { label: 'Ready for Billing', className: `bg-emerald-100 text-emerald-800 border-emerald-300 ${BASE}` },
  READY_FOR_BILLING: { label: 'Ready for Billing', className: `bg-emerald-100 text-emerald-800 border-emerald-300 ${BASE}` },
  BILLED:          { label: 'Billed',           className: `bg-slate-200 text-slate-700 border-slate-400 ${BASE}` },
  // Phase 18 M13: a finalized supervisory visit (never billed)
  COMPLETE:        { label: 'Complete',         className: `bg-teal-100 text-teal-800 border-teal-300 ${BASE}` },
  CANCELLED:       { label: 'Cancelled',        className: `bg-stone-100 text-stone-600 border-stone-200 ${BASE}` },
};

const DEFAULT_CONFIG: VisitStatusConfig = {
  label: '',
  className: `bg-slate-200 text-slate-700 border-slate-400 ${BASE}`,
};

// ─── Utility functions ────────────────────────────────────────────────────────

/**
 * Look up the canonical config for a visit status string.
 * Normalises to uppercase with underscores; falls back to a slate default.
 */
export function getVisitStatusConfig(status: string): VisitStatusConfig {
  if (!status) return { ...DEFAULT_CONFIG, label: status };
  const key = status.trim().toUpperCase().replace(/[\s-]+/g, '_');
  return VISIT_STATUS_MAP[key] ?? { ...DEFAULT_CONFIG, label: status };
}

/**
 * Returns the full Tailwind class string for a visit status pill.
 */
export function getVisitStatusPillClassName(status: string): string {
  return getVisitStatusConfig(status).className;
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface VisitStatusPillProps {
  /** Visit status string: SCHEDULED | INPROGRESS | DOCPENDING | READYFORBILLING | BILLED | COMPLETE | CANCELLED */
  status: string;
  /** Override display label */
  label?: string;
  /** Extra Tailwind classes */
  className?: string;
}

/**
 * VisitStatusPill — canonical visit status badge component.
 *
 * Renders as: font-semibold · text-xs · rounded · border + pastel bg with
 * colored text and matching border.
 */
export function VisitStatusPill({ status, label, className = '' }: VisitStatusPillProps) {
  const config = getVisitStatusConfig(status);
  const displayLabel = label ?? config.label;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 ${config.className} ${className}`.trim()}>
      {displayLabel}
    </span>
  );
}

export default VisitStatusPill;
